import { useManagerProfilesRetrieve, useUsersRetrieve } from "@sidegigllc/api/client";
import { useQuery } from "@tanstack/react-query";
import { Outlet } from "@tanstack/react-router";
import { activeLocationsQueryOptions } from "api/queries/locations";
import { LoaderLogo } from "components/Loader/logo";
import { pageConversationsAtom } from "domains/aiChat/lib/storage";
import { useLinkableAccountsStatus } from "domains/auth/providers/Root/useLinkableAccountStatus";
import { useSession } from "domains/auth/queries/session";
import { editNotesAtom, pageLinesAtom, postDraftsAtom, undoAtom } from "domains/postGig/redesign/draft-store";
import { useToSAcceptance } from "domains/signUp/api/tos";
import { AccountLinkingDialog } from "domains/signUp/containers/AccountLinkingDialog";
import { ToSDialog } from "domains/signUp/containers/ToSDialog";
import { useSetAtom } from "jotai";
import React from "react";
import { useActiveLocationId } from "services/locationOrgContext";
import { getAnalyticsUserTraits, identify, track } from "utils/analytics";
import { AuthContext, type IAuthContext } from "./context";
import { AUTH_STALE_TIME, deriveOnboardingGates, isExpectedError, selectAuthUser } from "./helpers";

// Session is gated in authedRoute.beforeLoad; this only wires user data + auth context.
export function AuthProvider() {
  return <UserDataProvider />;
}

function UserDataProvider() {
  const { signOut } = useSession();
  const clearPostDrafts = useSetAtom(postDraftsAtom);
  const clearPostUndo = useSetAtom(undoAtom);
  const clearPostNotes = useSetAtom(editNotesAtom);
  const clearPostLines = useSetAtom(pageLinesAtom);
  const clearPageConversations = useSetAtom(pageConversationsAtom);
  const { data: user, refetch: refetchUser } = useUsersRetrieve("me", {
    query: {
      retry: (count, error) => (isExpectedError(error) ? false : count <= 1),
      staleTime: AUTH_STALE_TIME,
      select: selectAuthUser,
    },
  });
  const {
    data: managerProfile,
    isLoading: profileLoading,
    refetch: refetchManagerProfile,
  } = useManagerProfilesRetrieve("me", {
    query: {
      retry: (count, error) => (isExpectedError(error) ? false : count < 1),
      refetchOnWindowFocus: false,
      staleTime: AUTH_STALE_TIME,
    },
  });
  // Usually prefetched by the auth root loader; on a fresh login it fetches here instead.
  const { data: activeLocations } = useQuery(activeLocationsQueryOptions());
  const [activeLocationId] = useActiveLocationId();

  const analyticsUser = user ? getAnalyticsUserTraits(user, managerProfile, activeLocationId) : undefined;

  const tosAcceptance = useToSAcceptance();
  const isTosAccepted = tosAcceptance.isLoading || tosAcceptance.data?.status === "ACCEPTED";

  React.useEffect(() => {
    if (analyticsUser) void identify(analyticsUser.id, analyticsUser);
  }, [analyticsUser]);

  const loaded = !!user && !profileLoading;
  const { hasLocations, signUpRequired, locationRequired } = deriveOnboardingGates({
    loaded,
    appMode: user?.app_mode,
    managerProfileId: managerProfile?.id,
    profileHasLocations: managerProfile?.has_locations,
    activeLocationCount: activeLocations?.length,
    isGigproAdmin: !!user?.is_gigpro_admin,
  });
  const linkableAccountsStatus = useLinkableAccountsStatus({ signUpRequired });

  const globalLoading = !user || linkableAccountsStatus === "loading";

  const contextValue = globalLoading
    ? undefined
    : ({
        linkableAccountsStatus,
        signUpRequired,
        tosAccepted: isTosAccepted,
        locationRequired,
        user,
        managerProfile,
        analyticsUser,
        hasLocations,
        logout: async () => {
          track("User Logout", {});
          // A draft in the browser belongs to the manager who made it.
          clearPostDrafts({});
          clearPostUndo({});
          clearPostNotes({});
          clearPostLines({});
          clearPageConversations({});
          await signOut();
        },
        refreshUser: async () => {
          const [user, managerProfile] = await Promise.all([refetchUser(), refetchManagerProfile()]);
          if (!(user.data && managerProfile.data)) return;
          return { user: user.data, managerProfile: managerProfile.data };
        },
      } satisfies IAuthContext);

  if (!contextValue) return <LoaderLogo />;

  return (
    <AuthContext.Provider value={contextValue}>
      <ToSDialog />
      <AccountLinkingDialog open={linkableAccountsStatus === "linking-required"} />
      <Outlet />
    </AuthContext.Provider>
  );
}
